import { useState, useRef, useEffect, type MouseEvent as ReactMouseEvent } from "react";
import { Link, useLocation } from "wouter";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";
import logoImg from "@assets/image_1776194131058.png";
import { serviceData } from "@/pages/services/serviceData";

const leistungenItems = serviceData.map((s) => ({ label: s.title, href: s.route }));


type ActiveMenu = "leistungen" | "zertifikate" | null;

interface NavbarProps {
  isScrolled: boolean;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export default function Navbar({ isScrolled, mobileMenuOpen, setMobileMenuOpen }: NavbarProps) {
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const [mobileExpanded, setMobileExpanded] = useState<ActiveMenu>(null);
  const [location, setLocation] = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const serviceRoutes = serviceData.map((s) => s.route);
  const isLeistungenArea = location.startsWith("/leistungen") || serviceRoutes.includes(location);
  const hasTransparentDesktopHeader = ["/garagentore/bretzfeld", "/garagentore/neuenstadt-am-kocher", "/garagentore/kupferzell"].includes(location) && !isScrolled;
  const hasLightTransparentDesktopHeader = location === "/garagentore/kupferzell" && !isScrolled;
  const keepsColorLogoOnTransparentHeader = ["/garagentore/bretzfeld", "/garagentore/neuenstadt-am-kocher"].includes(location);
  const transparentDesktopLinkClass = hasLightTransparentDesktopHeader ? "text-[#173122] hover:text-primary" : "text-white hover:text-green-300";

  const openMenu = (menu: ActiveMenu) => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setActiveMenu(menu);
  };

  const scheduleClose = () => {
    closeTimeout.current = setTimeout(() => setActiveMenu(null), 120);
  };

  const cancelClose = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
  };

  const closeAll = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setActiveMenu(null);
  };

  const handleLogoClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    closeAll();
    setMobileMenuOpen(false);

    if (location === "/") {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSectionClick = (event: ReactMouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    closeAll();
    setMobileMenuOpen(false);

    if (location === "/" || location === "") {
      window.history.pushState(null, "", `/#${id}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      return;
    }

    sessionStorage.setItem("scrollTarget", id);
    setLocation("/");
  };

  const toggleMobileAccordion = (menu: ActiveMenu) => {
    setMobileExpanded((prev) => (prev === menu ? null : menu));
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        closeAll();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    closeAll();
    setMobileExpanded(null);
  }, [location]);

  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-white/97 backdrop-blur-md py-2.5 shadow-[0_8px_28px_rgba(0,0,0,0.07),0_1px_0_rgba(0,0,0,0.08)]"
          : hasTransparentDesktopHeader
            ? "bg-white py-3.5 shadow-[0_1px_0_rgba(0,0,0,0.10)] md:bg-transparent md:shadow-none"
            : "bg-white py-3.5 shadow-[0_1px_0_rgba(0,0,0,0.10)]"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
            <img
              src={logoImg}
              alt="Tür- und Torservice Britz"
              className={`w-auto object-contain transition-all duration-500 ${
                isScrolled ? "h-14 md:h-16" : `h-16 md:h-[70px] ${hasTransparentDesktopHeader && !hasLightTransparentDesktopHeader && !keepsColorLogoOnTransparentHeader ? "md:brightness-0 md:invert md:drop-shadow-md" : "md:filter-none"}`
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {/* Leistungen Trigger */}
            <div
              className="relative"
              onMouseEnter={() => openMenu("leistungen")}
              onMouseLeave={scheduleClose}
            >
              <Link
                href="/leistungen"
                className={`flex items-center gap-1.5 text-lg font-semibold transition-colors ${
                  isLeistungenArea || activeMenu === "leistungen"
                    ? "text-primary font-semibold"
                    : hasTransparentDesktopHeader ? transparentDesktopLinkClass : "hover:text-primary text-foreground"
                }`}
                onClick={closeAll}
              >
                Leistungen
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    activeMenu === "leistungen" ? "rotate-180" : ""
                  }`}
                />
              </Link>
            </div>

            <a
              href="/#ueber-uns"
              onClick={(event) => handleSectionClick(event, "ueber-uns")}
              className={`text-lg font-semibold transition-colors ${hasTransparentDesktopHeader ? transparentDesktopLinkClass : "hover:text-primary"}`}
            >
              Über uns
            </a>

            {/* Zertifikate Link */}
            <Link
              href="/zertifikate"
              onClick={closeAll}
              className={`text-lg font-semibold transition-colors ${
                location === "/zertifikate"
                  ? "text-primary font-semibold"
                  : hasTransparentDesktopHeader ? transparentDesktopLinkClass : "hover:text-primary text-foreground"
              }`}
            >
              Zertifikate
            </Link>

            <a
              href="/#kontakt"
              onClick={(event) => handleSectionClick(event, "kontakt")}
              className={`text-lg font-semibold transition-colors ${hasTransparentDesktopHeader ? transparentDesktopLinkClass : "hover:text-primary"}`}
            >
              Kontakt
            </a>

            <Button asChild className="h-12 rounded-xl px-5 gap-2.5 text-base font-bold shadow-[0_10px_22px_rgba(42,143,48,0.22)]">
              <a href="tel:0794133514">
                <Phone className="w-5 h-5" />
                07941 33 51 4
              </a>
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ── Desktop Mega Menu Panel ── */}
      <AnimatePresence>
        {activeMenu && (
          <motion.div
            key={activeMenu}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="hidden md:block absolute top-full left-0 right-0 bg-white border-t border-zinc-100 shadow-lg"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className="container mx-auto px-4 md:px-6 py-8">
              {activeMenu === "leistungen" && (
                <>
                  <div className="mb-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-600">
                      Unsere Leistungen
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-x-10 gap-y-1">
                    {leistungenItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="flex items-center gap-2.5 py-2.5 text-sm text-zinc-700 hover:text-primary font-medium transition-colors group border-b border-zinc-50 hover:border-primary/20"
                        onClick={closeAll}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/30 group-hover:bg-primary transition-colors shrink-0" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Mobile Nav ── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="md:hidden overflow-hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-border"
          >
            <div className="flex flex-col px-4 pb-4 pt-2">

              {/* Leistungen Accordion */}
              <div className="border-b border-border">
                <button
                  className={`w-full text-left flex items-center justify-between font-medium py-3.5 text-sm ${
                    isLeistungenArea ? "text-primary" : "text-foreground"
                  }`}
                  onClick={() => toggleMobileAccordion("leistungen")}
                >
                  Leistungen
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      mobileExpanded === "leistungen" ? "rotate-180 text-primary" : "text-zinc-500"
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileExpanded === "leistungen" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pb-3 pl-3 flex flex-col">
                        <Link
                          href="/leistungen"
                          className="py-2 text-xs font-bold text-primary uppercase tracking-wider"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Alle Leistungen ansehen →
                        </Link>
                        {leistungenItems.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            className="py-2 text-sm text-zinc-600 hover:text-primary transition-colors border-b border-zinc-50 last:border-0"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Über uns */}
              <a
                href="/#ueber-uns"
                onClick={(event) => handleSectionClick(event, "ueber-uns")}
                className="text-left text-sm font-medium py-3.5 border-b border-border text-foreground"
              >
                Über uns
              </a>

              {/* Zertifikate Link */}
              <Link
                href="/zertifikate"
                className={`text-left text-sm font-medium py-3.5 border-b border-border block ${
                  location === "/zertifikate" ? "text-primary" : "text-foreground"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Zertifikate
              </Link>

              {/* Kontakt */}
              <a
                href="/#kontakt"
                onClick={(event) => handleSectionClick(event, "kontakt")}
                className="text-left text-sm font-medium py-3.5 border-b border-border text-foreground"
              >
                Kontakt
              </a>

              <div className="pt-4">
                <Button asChild className="w-full gap-2 justify-center">
                  <a href="tel:0794133514">
                    <Phone className="w-4 h-4" />
                    07941 33 51 4
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
